import { useContext, useMemo } from "react";

import { GROUP_ITEMS_KEY_REQUIRED_ARGS } from "~/actionItems/actionItemGroup/ActionItemGroupsList";
import type { FilterType } from "~/actionItems/filters/Filters";
import { ActionItemsContext } from "~/actionItems/newActionItems/ActionItemsContext";

/*
 * Generates a Relay record key string to select Action Items based on filters.
 *
 * This is exported separately so it can also be used in code that lives within the
 * ActionItemState, which defined ActionItemsContext - in that scope, using the
 * hook below doesn't work as expected because the hook refers to the context it's
 * called in.
 */
export function composeItemsKeyString(filters: Partial<FilterType>): string {
	const requiredFilters = GROUP_ITEMS_KEY_REQUIRED_ARGS;

	// The key needs to include all the required args and be in alphabetical order
	const args: Array<string> = [];

	for (const [key, filter] of Object.entries(requiredFilters)) {
		if (filters[filter] !== undefined) {
			args.push(`${key}:${filters[filter]}`);
		} else {
			console.warn(`Unknown filter: ${filter}. Please add it to the filters list.`);
		}
	}

	return `items(${args.sort().join(",")})`;
}

/*
 * Provides a GraphQL record key that contains filter required filter states.
 *
 * This is used in the context of making optimistic updates to the store after an update
 * to avoid fetching Action Items all over again - drift between the required keys and
 * the ones provided here will cause local update logic to fail to find where
 * to insert new items as it they are created.
 */
const useActionItemGroupItemsKey = (): string => {
	const {
		filters: { showAIDetected, showCompleted, showCreatedByYou, showRecentOnly, showWontDo },
	} = useContext(ActionItemsContext);

	const keyArgs = useMemo(
		() => composeItemsKeyString({ showAIDetected, showCompleted, showCreatedByYou, showRecentOnly, showWontDo }),
		[showAIDetected, showCompleted, showCreatedByYou, showRecentOnly, showWontDo],
	);

	return keyArgs;
};

export default useActionItemGroupItemsKey;
